<template>
  <main class="edit-admin-page">
    <div class="edit-shell">
      <router-link to="/admin/user" class="back-link"
        ><i class="fas fa-arrow-left" aria-hidden="true"></i> Back to admin directory</router-link
      >
      <header class="page-heading">
        <p class="eyebrow">Account management</p>
        <h1>Edit administrator</h1>
        <p>Keep profile information and administrative access up to date.</p>
      </header>

      <div v-if="adminId && canEditSelectedAdmin" class="edit-layout">
        <form class="editor-card" @submit.prevent="update">
          <fieldset :disabled="saving">
            <section class="form-section" aria-labelledby="account-heading">
              <div class="section-heading">
                <span class="section-number">01</span>
                <div>
                  <h2 id="account-heading">Account details</h2>
                  <p>The identity and contact details for this administrator.</p>
                </div>
              </div>
              <div class="field-grid">
                <div class="field">
                  <label for="admin-username">Username</label
                  ><input
                    id="admin-username"
                    v-model="username"
                    type="text"
                    autocomplete="off"
                    placeholder="Enter username"
                  />
                </div>
                <div class="field">
                  <label for="admin-email">Email address</label
                  ><input
                    id="admin-email"
                    v-model="email"
                    type="email"
                    autocomplete="off"
                    placeholder="name@organization.com"
                  />
                </div>
                <div class="field">
                  <label for="admin-owner">Organization / owner</label
                  ><input
                    id="admin-owner"
                    v-model="owner"
                    type="text"
                    placeholder="Enter organization name"
                  />
                </div>
                <div class="field">
                  <label for="admin-position">Position</label
                  ><input
                    id="admin-position"
                    v-model="position"
                    type="text"
                    placeholder="Enter position or job title"
                  />
                </div>
              </div>
            </section>
            <section class="form-section access-section" aria-labelledby="access-heading">
              <div class="section-heading">
                <span class="section-number">02</span>
                <div>
                  <h2 id="access-heading">Access settings</h2>
                  <p>Review the administrative role assigned to this account.</p>
                </div>
              </div>
              <div class="field">
                <label for="admin-role">Authorization</label
                ><select id="admin-role" v-model="role">
                  <option disabled value="">Select an admin role</option>
                  <option v-if="hasCustomRole" :value="role">{{ currentRoleLabel }}</option>
                  <option v-for="option in roleOptions" :key="option" :value="option">
                    {{ option }}
                  </option>
                </select>
                <p class="field-hint">Changes take effect when you save this profile.</p>
              </div>
            </section>
          </fieldset>
          <div class="form-footer">
            <p v-if="saveError" class="save-error" role="alert">{{ saveError }}</p>
            <div class="footer-actions">
              <span class="save-note"
                ><i class="far fa-save" aria-hidden="true"></i> Review your changes before
                saving</span
              >
              <div class="button-group">
                <router-link v-if="!saving" to="/admin/user" class="cancel-button"
                  >Cancel</router-link
                ><button class="save-button" type="submit" :disabled="saving">
                  <b-spinner v-if="saving" small /><i
                    v-else
                    class="fas fa-check"
                    aria-hidden="true"
                  ></i
                  >{{ saving ? 'Saving changes…' : 'Save changes' }}
                </button>
              </div>
            </div>
          </div>
        </form>

        <aside class="profile-preview" aria-label="Administrator profile preview">
          <div class="preview-cover"><span class="preview-tag">Profile preview</span></div>
          <div class="preview-body">
            <div class="preview-avatar" aria-hidden="true">{{ initials }}</div>
            <h2>{{ username || 'Administrator' }}</h2>
            <p class="preview-email">{{ email || 'Email not provided' }}</p>
            <span class="role-badge">{{ roleLabel }}</span>
            <dl>
              <div>
                <dt>Organization</dt>
                <dd>{{ owner || 'Not provided' }}</dd>
              </div>
              <div>
                <dt>Position</dt>
                <dd>{{ position || 'Not provided' }}</dd>
              </div>
            </dl>
            <p class="preview-note">
              <i class="far fa-user" aria-hidden="true"></i> Preview reflects the details in this
              form.
            </p>
          </div>
        </aside>
      </div>
      <section v-else-if="adminId" class="empty-card">
        <span class="empty-icon"><i class="fas fa-lock" aria-hidden="true"></i></span>
        <h2>This administrator account is protected</h2>
        <p>Only a Head Admin can update Head Admin account details.</p>
        <router-link to="/admin/user" class="save-button"
          >Back to admin directory <i class="fas fa-arrow-left" aria-hidden="true"></i
        ></router-link>
      </section>
      <section v-else class="empty-card">
        <span class="empty-icon"><i class="far fa-user" aria-hidden="true"></i></span>
        <h2>Select an administrator to edit</h2>
        <p>Open an account from the admin directory to view and update its details.</p>
        <router-link to="/admin/user" class="save-button"
          >Go to admin directory <i class="fas fa-arrow-right" aria-hidden="true"></i
        ></router-link>
      </section>
    </div>
  </main>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';

export default {
  name: 'EditAdminPage',
  data() {
    return {
      adminId: null,
      owner: '',
      position: '',
      username: '',
      email: '',
      role: '',
      currentRoleLabel: '',
      saving: false,
      saveError: '',
      roleOptions: [
        'Super Admin',
        'International Cooperation Admin',
        'Monitoring and Evaluation Admin',
        'Non-Governmental Organization Admin',
        'Organization Admin',
      ],
    };
  },
  computed: {
    ...mapGetters('admin', ['getAdminById']),
    ...mapGetters('auth', ['user']),
    selectedAdmin() {
      return this.getAdminById?.data || null;
    },
    isHeadAdminViewer() {
      return this.isHeadAdmin(this.user);
    },
    canEditSelectedAdmin() {
      return this.isHeadAdminViewer;
    },
    hasCustomRole() {
      return this.role !== '' && !this.roleOptions.includes(this.role);
    },
    roleLabel() {
      return (this.hasCustomRole ? this.currentRoleLabel : this.role) || 'No role selected';
    },
    initials() {
      return this.username.trim().slice(0, 2).toUpperCase() || 'AD';
    },
  },
  mounted() {
    const admin = this.selectedAdmin;
    if (!admin?.id) return;
    this.adminId = admin.id;
    this.owner = admin.organization || '';
    this.position = admin.position || '';
    this.username = admin.user?.username || admin.username || '';
    this.email = admin.user?.email || admin.email || '';
    this.role =
      admin.authorization?.value || admin.authorization?.display || admin.authorization || '';
    this.currentRoleLabel = admin.authorization?.display || String(this.role);
  },
  methods: {
    ...mapActions('admin', ['UPDATE_ADMIN']),
    isHeadAdmin(admin = {}) {
      const userType = Number(admin.user?.type ?? admin.type);

      return userType === 6;
    },
    async update() {
      if (this.saving || !this.adminId || !this.canEditSelectedAdmin) return;
      this.saving = true;
      this.saveError = '';
      try {
        await this.UPDATE_ADMIN({
          id: this.adminId,
          data: {
            organization: this.owner,
            user1: { username: this.username, email: this.email },
            authorization: this.role,
            position: this.position,
          },
        });
      } catch (error) {
        this.saveError = 'Unable to save changes. Please try again.';
        return;
      } finally {
        this.saving = false;
      }
      this.$swal('Admin updated!');
      this.$router.push('/admin/user');
    },
  },
};
</script>

<style scoped>
.edit-admin-page {
  min-height: 100vh;
  padding: 1.75rem 1.25rem 3rem;
  background: radial-gradient(ellipse at top right, #e8efe5, transparent 55%), #f6f8f5;
  color: #29412f;
}
.edit-shell {
  max-width: 1200px;
  margin: 0 auto;
}
.back-link {
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  margin-bottom: 2rem;
  color: #6c8067;
  font-size: 0.8rem;
  font-weight: 600;
  text-decoration: none;
}
.back-link:hover {
  color: #2e5c39;
}
.eyebrow {
  margin-bottom: 0.6rem;
  color: #708969;
  font-size: 0.67rem;
  font-weight: 700;
  letter-spacing: 0.15em;
  text-transform: uppercase;
}
.page-heading {
  margin-bottom: 2rem;
}
.page-heading h1 {
  margin: 0;
  color: #233e2b;
  font-size: clamp(1.9rem, 3.5vw, 2.7rem);
  font-weight: 700;
  letter-spacing: -0.045em;
}
.page-heading > p:last-child {
  margin: 0.85rem 0 0;
  color: #81907a;
  font-size: 0.9rem;
  line-height: 1.7;
}
.edit-layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 310px;
  gap: 1.5rem;
  align-items: start;
}
.editor-card {
  min-width: 0;
  border: 1px solid #e0e7da;
  border-radius: 20px;
  background: #fff;
  overflow: hidden;
  box-shadow: 0 10px 40px #24452a05;
}
.editor-card fieldset {
  border: 0;
  padding: 0;
  margin: 0;
  min-width: 0;
}
.form-section {
  padding: 2rem;
}
.section-heading {
  display: flex;
  align-items: flex-start;
  gap: 0.85rem;
  margin-bottom: 1.75rem;
}
.section-number {
  display: grid;
  place-items: center;
  flex-shrink: 0;
  width: 34px;
  height: 34px;
  border: 1px solid #dfe8d5;
  border-radius: 10px;
  background: #f2f6ec;
  color: #729057;
  font-size: 0.7rem;
  font-weight: 600;
}
.section-heading h2 {
  margin: 0.1rem 0 0.4rem;
  font-size: 1.05rem;
  font-weight: 700;
  letter-spacing: -0.02em;
}
.section-heading p {
  margin: 0;
  color: #8a9682;
  font-size: 0.78rem;
  line-height: 1.65;
}
.field-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1.5rem;
}
.field {
  min-width: 0;
}
.field label {
  display: block;
  margin-bottom: 0.6rem;
  font-size: 0.77rem;
  font-weight: 600;
  color: #506848;
}
.field input,
.field select {
  width: 100%;
  min-height: 46px;
  padding: 0.75rem 0.85rem;
  border: 1px solid #dfe6d9;
  border-radius: 9px;
  background: #fcfdfb;
  color: #364d2d;
  font-size: 0.85rem;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.field input::placeholder {
  color: #a3ac9c;
  font-size: 0.8rem;
}
.field input:focus,
.field select:focus {
  outline: none;
  border-color: #93ad7f;
  box-shadow: 0 0 0 3px #eef4e6;
  background: #fff;
}
.field-hint {
  margin: 0.65rem 0 0;
  font-size: 0.73rem;
  line-height: 1.65;
  color: #97a18f;
}
.access-section {
  border-top: 1px solid #edf0e8;
}
.form-footer {
  padding: 1.3rem 2rem;
  border-top: 1px solid #e9eee2;
  background: #fbfcf8;
}
.footer-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 1rem;
}
.save-note {
  display: flex;
  gap: 0.45rem;
  align-items: center;
  color: #909d83;
  font-size: 0.7rem;
}
.button-group {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-left: auto;
}
.cancel-button {
  color: #7c8d6f;
  font-size: 0.8rem;
  font-weight: 600;
  padding: 0.65rem 0.3rem;
}
.save-button {
  display: inline-flex;
  justify-content: center;
  align-items: center;
  gap: 0.6rem;
  width: auto;
  padding: 0.85rem 1.1rem;
  border: 1px solid #2b5134;
  border-radius: 9px;
  background: linear-gradient(135deg, #294b32, #406345);
  color: #fff;
  font-size: 0.78rem;
  font-weight: 600;
  text-decoration: none;
  box-shadow: 0 5px 12px #24462712;
}
.save-button:hover {
  background: #25442d;
  color: #fff;
}
.save-button:disabled {
  opacity: 0.65;
  cursor: wait;
}
.save-error {
  color: #a73d34;
  font-size: 0.85rem;
  line-height: 1.6;
}
.profile-preview {
  min-width: 0;
  overflow: hidden;
  border-radius: 20px;
  border: 1px solid #dfe7d8;
  background: #fff;
}
.preview-cover {
  height: 105px;
  padding: 1.2rem;
  background: radial-gradient(ellipse at top right, #57704c, transparent 80%), #2a4933;
  text-align: right;
}
.preview-tag {
  display: inline-block;
  padding: 0.35rem 0.6rem;
  border: 1px solid #ffffff28;
  border-radius: 6px;
  color: #d0dfc3;
  font-size: 0.6rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}
.preview-body {
  padding: 0 1.5rem 1.5rem;
  overflow-wrap: anywhere;
}
.preview-avatar {
  position: relative;
  display: grid;
  place-items: center;
  width: 62px;
  height: 62px;
  margin-top: -31px;
  margin-bottom: 1.1rem;
  border: 4px solid #fff;
  border-radius: 17px;
  background: #eff4e8;
  color: #607748;
  font-size: 1.2rem;
  font-weight: 600;
}
.preview-body h2 {
  margin: 0 0 0.4rem;
  font-size: 1.15rem;
  font-weight: 700;
}
.preview-email {
  color: #8a9780;
  font-size: 0.78rem;
  line-height: 1.6;
  margin: 0 0 1rem;
}
.role-badge {
  display: inline-block;
  border: 1px solid #e0e9d4;
  border-radius: 6px;
  padding: 0.35rem 0.6rem;
  background: #f3f7ed;
  color: #67844b;
  font-size: 0.69rem;
  font-weight: 600;
}
.preview-body dl {
  margin: 1.5rem 0;
  padding: 1.25rem 0;
  border-top: 1px solid #edf0e8;
  border-bottom: 1px solid #edf0e8;
}
.preview-body dl > div + div {
  margin-top: 1.15rem;
}
.preview-body dt {
  color: #91a083;
  font-size: 0.68rem;
  font-weight: 500;
  margin-bottom: 0.35rem;
}
.preview-body dd {
  color: #526a43;
  font-size: 0.8rem;
  line-height: 1.6;
  margin: 0;
}
.preview-note {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  margin: 0;
  font-size: 0.68rem;
  color: #96a288;
  line-height: 1.7;
}
.preview-note i {
  margin-top: 0.25rem;
}
.empty-card {
  padding: 3rem 1.5rem;
  text-align: center;
  background: #fff;
  border: 1px solid #e0e7da;
  border-radius: 20px;
}
.empty-icon {
  display: block;
  color: #779765;
  font-size: 1.7rem;
  margin-bottom: 1rem;
}
.empty-card h2 {
  font-size: 1.3rem;
}
.empty-card p {
  color: #81907a;
  font-size: 0.9rem;
  margin: 1rem 0 1.5rem;
}
a:focus-visible,
button:focus-visible {
  outline: 3px solid #91af78;
  outline-offset: 3px;
}
@media (max-width: 1100px) {
  .edit-layout {
    grid-template-columns: minmax(0, 1fr) 270px;
  }
  .form-section {
    padding: 1.5rem;
  }
}
@media (max-width: 991px) {
  .edit-admin-page {
    padding-top: 0;
  }
  .edit-layout {
    grid-template-columns: minmax(0, 1fr);
  }
  .profile-preview {
    max-width: 100%;
  }
}
@media (max-width: 575px) {
  .edit-admin-page {
    padding: 0 0 2rem;
  }
  .field-grid {
    grid-template-columns: minmax(0, 1fr);
    gap: 1.25rem;
  }
  .form-section,
  .form-footer {
    padding: 1.25rem;
  }
  .button-group {
    width: 100%;
    justify-content: space-between;
  }
  .save-note {
    width: 100%;
  }
}
</style>
